import ATLogo from 'assets/AT.svg';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { routes } from 'app/routes';
import { PhoneIconWithFill } from 'app/theme/Icons/PhoneIconWithFill';
import { breakpoint } from 'app/theme/theme';
import { VisuallyHidden } from 'pkg/components/VisuallyHidden';
import { contactData } from 'pkg/components/layout/contactData';

import { CurrencySelector } from './CurrencySelector';

const Container = styled.header`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.white};
  box-shadow: 0 0.9rem 1.7rem 0.2rem rgba(0, 0, 0, 0.27);
  min-height: 11rem;

  ${breakpoint.up.md`
    min-height: 13rem;
  `}
`;

const HeaderContent = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  position: relative;

  ${breakpoint.up.md`   
    flex-wrap: nowrap;
    padding: 1rem 12rem;
  `}

  ${breakpoint.up.lg`
    padding: 0;
    max-width: 1100px;
    margin: 0 auto;
  `}
`;

const LogoLink = styled.a`
  display: block;
  cursor: pointer;
  order: 2;
  ${breakpoint.up.md`
    order: 1;
  `}
`;

const Logo = styled.img`
  height: 5.5rem;

  ${breakpoint.up.lg`
    width: 19rem;
    height: auto;
  `}
`;

const PhoneNumberContainer = styled.a`
  color: ${({ theme }) => theme.palette.black.primary};
  background-color: #f1c44c;
  border-radius: 3.5rem;
  padding: 0.5rem 0.7rem;
  display: flex;
  align-items: center;
  line-height: 2.2rem;
  white-space: nowrap;
  transition: 0.3s transform;
  font-size: 1.3rem;
  justify-content: center;

  svg {
    margin-right: 0.5rem;
  }

  &:hover {
    color: ${({ theme }) => theme.palette.black.primary};
    transform: scale(1.1);
  }

  ${breakpoint.up.md`
    gap: 1rem;
    font-size: 1.6rem;
    padding: 1rem 1.5rem;
  `};

  svg {
    width: 12px;
    height: 12px;
    ${breakpoint.up.md`
      width: 20px;
      height: 20px;
    `};
  }
`;

const PhoneWithCurrencySelector = styled.div`
  display: flex;
  flex-direction: column;
  order: 3;
`;

export const Header = (): JSX.Element => {
  const location = useLocation();
  const isForm =
    location.pathname.includes(routes.TRANSLATION_QUOTE.FORM) ||
    location.pathname.includes(routes.INTERPRETING_QUOTE) ||
    location.pathname.includes(routes.PROOFREADING_QUOTE) ||
    location.pathname.includes(routes.MULTILINGUALDTP_QUOTE) ||
    location.pathname.includes(routes.AUDIO_TRANSCRIPTION_QUOTE) ||
    location.pathname.includes(routes.COPYWRITING_QUOTE) ||
    location.pathname.includes(routes.SUBTITLING_QUOTE) ||
    location.pathname.includes(routes.VOICE_OVER_QUOTE);

  const redirectToExternalPage = () => {
    window.open('https://www.absolutetranslations.com', '_self');
  };

  useEffect(() => {
    document.body.scrollIntoView();
  }, [location.pathname]);

  return (
    <Container>
      <HeaderContent>
        <LogoLink aria-label="Home page" onClick={redirectToExternalPage}>
          <Logo src={ATLogo} alt="Go to Absolute Translations home page" />
        </LogoLink>

        <PhoneWithCurrencySelector>
          <PhoneNumberContainer href={`tel:${contactData.phoneNumber}`}>
            <VisuallyHidden>Office phone number:</VisuallyHidden>
            <PhoneIconWithFill />
            {contactData.phoneNumber}
          </PhoneNumberContainer>

          {isForm && <CurrencySelector />}
        </PhoneWithCurrencySelector>
      </HeaderContent>
    </Container>
  );
};
